import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { useRetroMode } from '../context/RetroModeContext'

// Subtle 3D tilt that follows the cursor, applied on mouse move within the
// element's own bounds. Skipped in retro mode / prefers-reduced-motion.
export const useTilt = ({ max = 8, scale = 1.03 } = {}) => {
  const ref = useRef(null)
  const { retroMode } = useRetroMode()

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (retroMode || reduceMotion) return undefined

    gsap.set(el, { transformPerspective: 600, transformStyle: 'preserve-3d' })

    const rotateX = gsap.quickTo(el, 'rotateX', {
      duration: 0.4,
      ease: 'power2.out',
    })
    const rotateY = gsap.quickTo(el, 'rotateY', {
      duration: 0.4,
      ease: 'power2.out',
    })
    const scaleTo = gsap.quickTo(el, 'scale', {
      duration: 0.4,
      ease: 'power2.out',
    })

    const handleMove = (event) => {
      const rect = el.getBoundingClientRect()
      const px = (event.clientX - rect.left) / rect.width - 0.5
      const py = (event.clientY - rect.top) / rect.height - 0.5
      rotateY(px * max * 2)
      rotateX(-py * max * 2)
      scaleTo(scale)
    }

    const handleLeave = () => {
      rotateX(0)
      rotateY(0)
      scaleTo(1)
    }

    el.addEventListener('mousemove', handleMove)
    el.addEventListener('mouseleave', handleLeave)
    return () => {
      el.removeEventListener('mousemove', handleMove)
      el.removeEventListener('mouseleave', handleLeave)
      gsap.set(el, { clearProps: 'transform,transformPerspective' })
    }
  }, [retroMode, max, scale])

  return ref
}
